/**
 * /staff/change-password
 *
 * Mandatory password-change screen for internal staff after an owner
 * force-resets their password. The user landed here because their login
 * response carried `force_password_reset: true`. They cannot leave until
 * they set a new password (or explicitly sign out).
 */
import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { LockKey } from "@phosphor-icons/react";

export default function StaffChangePassword() {
  const nav = useNavigate();
  const [current, setCurrent] = useState("");
  const [next, setNext]       = useState("");
  const [confirm, setConfirm] = useState("");
  const [err, setErr]         = useState("");
  const [busy, setBusy]       = useState(false);

  const signOut = async () => {
    try { await api.post("/internal/auth/logout"); } catch { /* noop */ }
    nav("/staff/login", { replace: true });
  };

  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    if (next.length < 10) { setErr("New password must be at least 10 characters."); return; }
    if (next !== confirm) { setErr("Passwords do not match."); return; }
    setBusy(true);
    try {
      await api.post("/internal/auth/change-password", {
        current_password: current,
        new_password:     next,
      });
      // Success — staff can now enter the console.
      nav("/owner/staff", { replace: true });
    } catch (e2) {
      setErr(e2?.response?.data?.detail || e2.message || "Could not change password");
    } finally { setBusy(false); }
  };

  return (
    <div
      className="min-h-screen bg-slate-900/60 backdrop-blur-sm flex items-center justify-center p-4"
      data-testid="staff-change-password-page"
    >
      <form
        onSubmit={submit}
        className="bg-white rounded-2xl shadow-2xl w-full max-w-md overflow-hidden"
        role="dialog"
        aria-modal="true"
        aria-label="Password change required"
      >
        <div className="px-6 py-5 border-b border-slate-100 flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-amber-100 text-amber-600 flex items-center justify-center">
            <LockKey size={20} weight="bold" />
          </div>
          <div>
            <div className="font-display font-bold text-[17px]">Set a new password</div>
            <div className="text-[12px] text-slate-500">
              Your platform administrator issued a temporary password. Choose a permanent one before continuing.
            </div>
          </div>
        </div>
        <div className="p-6 space-y-3">
          <input
            type="password" required autoComplete="current-password" className="input w-full"
            placeholder="Temporary password"
            value={current} onChange={(e) => setCurrent(e.target.value)}
            data-testid="staff-change-pw-current"
          />
          <input
            type="password" required autoComplete="new-password" className="input w-full"
            placeholder="New password (min 10 characters)"
            value={next} onChange={(e) => setNext(e.target.value)}
            data-testid="staff-change-pw-new"
          />
          <input
            type="password" required autoComplete="new-password" className="input w-full"
            placeholder="Confirm new password"
            value={confirm} onChange={(e) => setConfirm(e.target.value)}
            data-testid="staff-change-pw-confirm"
          />
          {err && (
            <div className="text-[13px] text-red-600" data-testid="staff-change-pw-error">{err}</div>
          )}
        </div>
        <div className="px-6 py-4 border-t border-slate-100 flex items-center justify-between">
          <button
            type="button"
            onClick={signOut}
            className="text-[12px] text-slate-500 hover:underline"
            data-testid="staff-change-pw-signout"
          >
            Sign out instead
          </button>
          <button
            type="submit"
            disabled={busy}
            className="btn-primary"
            data-testid="staff-change-pw-submit"
          >
            {busy ? "Saving…" : "Set new password"}
          </button>
        </div>
      </form>
    </div>
  );
}
